/**
 * Port of legacy HALO's `Amenities` model constructor. The byMaloneLotIdEncodedV2
 * API returns `amenities.highlightedAmenities` (nested persisted/subtypes objects)
 * and `amenities.petPolicies` (array of { petType, ... }). Legacy HALO ran a
 * transformation step that produced `amenities.highlights` ({ id, display,
 * types[] }), a `condensedAmenities` map, and a keyed `petPolicies` object
 * ({ cat, dog }) — the shape every downstream HALO component was written for.
 *
 * We run the equivalent transformation inside `useListingDetail` so the ported
 * modules don't need to know which shape the API is in.
 */

import { petSizeSortRank } from './petSize';

type HighlightedAmenitySubtype = { persisted?: string; display?: string };

type HighlightedAmenity = {
  persisted?: string;
  display?: string;
  subtypes?: Array<HighlightedAmenitySubtype>;
};

type RawPetPolicy = {
  petType?: string;
  petSize?: string;
  allowed?: boolean;
  restriction?: string;
  comment?: string;
  maxNumberAllowed?: number;
  maxWeightAllowed?: number;
  oneTimeFee?: number;
  monthlyFee?: number;
  additionalFee?: number;
};

type NormalizedHighlight = {
  id: string;
  display: string;
  types: Array<string>;
};

type NormalizedPetPolicyMap = {
  cat?: Array<RawPetPolicy>;
  dog?: Array<RawPetPolicy>;
};

type CondensedAmenities = {
  pets?: string;
  laundry?: string;
  security?: string;
  airConditioning?: boolean;
  dishwasher?: boolean;
  parking?: boolean;
  gym?: boolean;
  pool?: boolean;
};

const AIR_CONDITIONING_CHOICES = new Set([
  'Central A/C',
  'Wall Mounted A/C',
  'Window Mounted A/C',
  'A/C',
  'Electric A/C',
  'Evaporative Cooling',
  'Gas Cooling',
  'Individual Cooling',
  'Refrigeration Cooling',
  'Solar Cooling',
  'Zoned Air Conditioning',
]);

function buildCondensedAmenities(
  highlightedAmenities: Array<HighlightedAmenity>,
): CondensedAmenities {
  const out: CondensedAmenities = {};
  for (const amenity of highlightedAmenities) {
    const persisted = amenity.persisted;
    const subtypes = amenity.subtypes ?? [];

    if (persisted === 'pets' && subtypes[0]?.display) {
      out.pets = subtypes[0].display;
    } else if (persisted === 'laundry') {
      out.laundry = subtypes
        .map((s) => (s.display?.toLowerCase() === 'no laundry' ? 'none' : s.display))
        .filter((v): v is string => Boolean(v))
        .join(', ');
    } else if (persisted === 'security') {
      out.security = subtypes.map((s) => s.display).filter(Boolean).join(', ');
    } else if (persisted === 'hvac') {
      if (subtypes.some((s) => s.display && AIR_CONDITIONING_CHOICES.has(s.display))) {
        out.airConditioning = true;
      }
    } else if (persisted === 'appliances') {
      if (subtypes.some((s) => s.persisted?.includes('dishwasher'))) {
        out.dishwasher = true;
      }
    } else if (persisted === 'parking') {
      if (!subtypes.some((s) => s.persisted?.includes('none'))) {
        out.parking = true;
      }
    } else if (persisted === 'gym') {
      out.gym = true;
    } else if (persisted === 'pool') {
      out.pool = true;
    }
  }
  return out;
}

function buildHighlights(
  highlightedAmenities: Array<HighlightedAmenity>,
): Array<NormalizedHighlight> {
  return highlightedAmenities
    .filter((h) => typeof h.persisted === 'string')
    .map((h) => ({
      id: h.persisted as string,
      display: h.display ?? '',
      types: (h.subtypes ?? [])
        .map((s) => s.display)
        .filter((v): v is string => typeof v === 'string'),
    }));
}

function buildPetPolicyMap(
  petPolicies: Array<RawPetPolicy> | null | undefined,
): NormalizedPetPolicyMap | null {
  if (!Array.isArray(petPolicies) || petPolicies.length === 0) return null;
  // Key by petType+petSize (one policy per size); last entry wins for exact duplicates.
  const bySizeKey = new Map<string, { petType: 'cat' | 'dog'; policy: RawPetPolicy }>();
  for (const policy of petPolicies) {
    const petType = policy.petType?.toLowerCase();
    if (petType === 'cat' || petType === 'dog') {
      bySizeKey.set(`${petType}:${policy.petSize?.toLowerCase() ?? ''}`, { petType, policy });
    }
  }
  const out: NormalizedPetPolicyMap = {};
  for (const { petType, policy } of bySizeKey.values()) {
    (out[petType] ??= []).push(policy);
  }
  out.cat?.sort((a, b) => petSizeSortRank(a.petSize) - petSizeSortRank(b.petSize));
  out.dog?.sort((a, b) => petSizeSortRank(a.petSize) - petSizeSortRank(b.petSize));
  return out;
}

export function normalizeAmenities(
  raw: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null | undefined {
  if (!raw) return raw;
  const highlightedAmenities = Array.isArray(raw.highlightedAmenities)
    ? (raw.highlightedAmenities as Array<HighlightedAmenity>)
    : [];
  const rawPetPolicies = raw.petPolicies;
  const petPoliciesArray = Array.isArray(rawPetPolicies)
    ? (rawPetPolicies as Array<RawPetPolicy>)
    : null;

  const alreadyNormalized =
    Array.isArray(raw.highlights) &&
    !Array.isArray(raw.petPolicies);

  if (alreadyNormalized) return raw;

  return {
    ...raw,
    highlights: buildHighlights(highlightedAmenities),
    condensedAmenities: buildCondensedAmenities(highlightedAmenities),
    petPolicies: buildPetPolicyMap(petPoliciesArray),
  };
}
